'use client';

import { useState, useEffect } from 'react';
import { Info, X } from 'lucide-react';

interface Hotspot {
  id: string;
  label: string;
  description?: string;
  position: { top: string; left: string };
  color?: string;
}

interface Props {
  productMetadata?: Record<string, any>;
}

export function HotspotOverlay({ productMetadata }: Props) {
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const configured = (productMetadata as any)?.hotspots;
    if (Array.isArray(configured) && configured.length > 0) {
      setHotspots(configured);
    } else {
      setHotspots([
        { id: 'h1', label: 'Material', description: 'High-quality material with UV-resistant coating', position: { top: '35%', left: '55%' }, color: '#3b82f6' },
        { id: 'h2', label: 'Joint', description: 'Precision-engineered joint for maximum durability', position: { top: '60%', left: '40%' }, color: '#10b981' },
        { id: 'h3', label: 'Finish', description: 'Hand-polished surface with matte or gloss options', position: { top: '45%', left: '70%' }, color: '#f59e0b' },
      ]);
    }
  }, [productMetadata]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && e.key === 'H') {
        e.preventDefault();
        setVisible((v) => !v);
      }
      if (e.key === 'Escape') setActiveId(null);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  if (hotspots.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setVisible((v) => !v)}
        className={`absolute top-3 z-10 h-8 w-8 flex items-center justify-center rounded-full backdrop-blur-md border transition-all duration-200 ${
          visible
            ? 'bg-white dark:bg-zinc-100 border-white/80 text-zinc-900 shadow-md shadow-black/10 right-[3.75rem]'
            : 'bg-black/25 dark:bg-white/10 border-white/10 text-white/70 hover:text-white hover:bg-black/35 dark:hover:bg-white/20 right-[3.75rem]'
        }`}
        title="Toggle hotspots (Ctrl+Shift+H)"
      >
        <Info className="h-3.5 w-3.5" />
      </button>

      {visible && (
        <div className="absolute inset-0 z-[5] pointer-events-none">
          {hotspots.map((hotspot) => {
            const isActive = activeId === hotspot.id;
            const color = hotspot.color || '#3b82f6';
            return (
              <div
                key={hotspot.id}
                className="absolute pointer-events-auto"
                style={{ top: hotspot.position.top, left: hotspot.position.left, transform: 'translate(-50%, -50%)' }}
              >
                <button
                  onClick={() => setActiveId(isActive ? null : hotspot.id)}
                  className="relative group"
                  aria-label={hotspot.label}
                >
                  <span
                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: color, animationDuration: '2s' }}
                  />
                  <span
                    className="relative flex h-6 w-6 items-center justify-center rounded-full text-white text-[9px] font-bold shadow-lg ring-2 ring-white/50 transition-transform hover:scale-125"
                    style={{ backgroundColor: color }}
                  >
                    {hotspot.label.charAt(0)}
                  </span>
                  <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-zinc-900/90 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {hotspot.label}
                  </span>
                </button>

                {isActive && (
                  <div
                    className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-52 bg-white dark:bg-zinc-900 rounded-lg shadow-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden z-20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 dark:border-zinc-800" style={{ backgroundColor: `${color}15` }}>
                      <span className="text-xs font-semibold" style={{ color }}>{hotspot.label}</span>
                      <button onClick={() => setActiveId(null)} className="text-zinc-400 hover:text-zinc-600">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    {hotspot.description && (
                      <div className="px-3 py-2">
                        <p className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">{hotspot.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
